package es.subastia.profile

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ExitToApp
import androidx.compose.material.icons.automirrored.filled.Help
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import androidx.compose.material.icons.filled.Notifications
import androidx.compose.material.icons.filled.Person
import androidx.compose.material.icons.filled.Settings
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import coil3.compose.AsyncImage
import es.subastia.auth.rememberGoogleSignInLauncher
import es.subastia.navigation.Navigator
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import subastia.composeapp.generated.resources.Res
import subastia.composeapp.generated.resources.btn_accept
import subastia.composeapp.generated.resources.btn_google_signin
import subastia.composeapp.generated.resources.btn_login
import subastia.composeapp.generated.resources.btn_logout
import subastia.composeapp.generated.resources.btn_signup
import subastia.composeapp.generated.resources.label_email
import subastia.composeapp.generated.resources.label_password
import subastia.composeapp.generated.resources.label_user
import subastia.composeapp.generated.resources.profile_item_help
import subastia.composeapp.generated.resources.profile_item_my_data
import subastia.composeapp.generated.resources.profile_item_notifications
import subastia.composeapp.generated.resources.profile_item_settings
import subastia.composeapp.generated.resources.text_create_account
import subastia.composeapp.generated.resources.text_have_account_login
import subastia.composeapp.generated.resources.text_or
import subastia.composeapp.generated.resources.title_create_account
import subastia.composeapp.generated.resources.title_error
import subastia.composeapp.generated.resources.title_welcome_back

@Composable
fun ProfileScreen(
    navigator: Navigator,
) {
    val viewModel: ProfileViewModel = koinViewModel()
    val user by viewModel.currentUser.collectAsStateWithLifecycle()

    val uiState by viewModel.uiState.collectAsStateWithLifecycle()

    val googleSignInLauncher = rememberGoogleSignInLauncher { result ->
        result.onSuccess { token ->
            viewModel.signInWithGoogle(token)
        }.onFailure { e ->
            viewModel.setError(e.message ?: "Google Sign-In failed")
        }
    }

    Column(
        modifier = Modifier.fillMaxSize().padding(16.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center,
    ) {
        if (uiState is ProfileUiState.Loading) {
            CircularProgressIndicator()
        } else {
            // Mostramos el diálogo de error si el estado es Error
            (uiState as? ProfileUiState.Error)?.let { error ->
                AlertDialog(
                    onDismissRequest = { viewModel.clearError() },
                    title = { Text(stringResource(Res.string.title_error)) },
                    text = { Text(error.message) },
                    confirmButton = {
                        TextButton(onClick = { viewModel.clearError() }) {
                            Text(stringResource(Res.string.btn_accept))
                        }
                    },
                )
            }

            if (user != null) {
                Column(
                    modifier = Modifier.fillMaxSize(),
                    horizontalAlignment = Alignment.CenterHorizontally,
                ) {
                    // Header with Avatar
                    Spacer(modifier = Modifier.height(64.dp))
                    Box(
                        modifier = Modifier
                            .size(100.dp)
                            .clip(CircleShape)
                            .background(MaterialTheme.colorScheme.primaryContainer),
                        contentAlignment = Alignment.Center,
                    ) {
                        if (user?.photoUrl != null) {
                            AsyncImage(
                                model = user?.photoUrl,
                                contentDescription = null,
                                modifier = Modifier.fillMaxSize(),
                                contentScale = ContentScale.Crop,
                            )
                        } else {
                            Text(
                                text = (user?.displayName ?: user?.email ?: "U").take(1).uppercase(),
                                style = MaterialTheme.typography.displayMedium,
                                color = MaterialTheme.colorScheme.onPrimaryContainer,
                            )
                        }
                    }

                    Spacer(modifier = Modifier.height(16.dp))

                    Text(
                        text = user?.displayName ?: stringResource(Res.string.label_user),
                        style = MaterialTheme.typography.headlineSmall,
                        fontWeight = FontWeight.Bold,
                    )

                    Text(
                        text = user?.email ?: "",
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )

                    Spacer(modifier = Modifier.height(32.dp))

                    // Menu Items
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .weight(1f),
                    ) {
                        ProfileMenuItem(
                            icon = Icons.Default.Person,
                            text = stringResource(Res.string.profile_item_my_data),
                            onClick = { /* TODO */ },
                        )
                        ProfileMenuItem(
                            icon = Icons.Default.Notifications,
                            text = stringResource(Res.string.profile_item_notifications),
                            onClick = { /* TODO */ },
                        )
                        ProfileMenuItem(
                            icon = Icons.Default.Settings,
                            text = stringResource(Res.string.profile_item_settings),
                            onClick = { /* TODO */ },
                        )
                        HorizontalDivider(modifier = Modifier.padding(vertical = 8.dp))
                        ProfileMenuItem(
                            icon = Icons.AutoMirrored.Filled.Help,
                            text = stringResource(Res.string.profile_item_help),
                            onClick = { /* TODO */ },
                        )
                    }

                    OutlinedButton(
                        onClick = { viewModel.logout() },
                        modifier = Modifier.fillMaxWidth().padding(bottom = 32.dp),
                        colors = ButtonDefaults.outlinedButtonColors(
                            contentColor = MaterialTheme.colorScheme.error,
                        ),
                    ) {
                        Icon(Icons.AutoMirrored.Filled.ExitToApp, contentDescription = null)
                        Spacer(modifier = Modifier.width(8.dp))
                        Text(stringResource(Res.string.btn_logout))
                    }
                }
            } else {
                var email by remember { mutableStateOf("") }
                var password by remember { mutableStateOf("") }
                var isRegistering by remember { mutableStateOf(false) }

                Text(
                    if (isRegistering) stringResource(Res.string.title_create_account) else stringResource(Res.string.title_welcome_back),
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold,
                )
                Spacer(modifier = Modifier.height(24.dp))

                OutlinedTextField(
                    value = email,
                    onValueChange = {
                        email = it
                        if (uiState is ProfileUiState.Error) viewModel.clearError()
                    },
                    label = { Text(stringResource(Res.string.label_email)) },
                    modifier = Modifier.fillMaxWidth(),
                )
                Spacer(modifier = Modifier.height(8.dp))
                OutlinedTextField(
                    value = password,
                    onValueChange = {
                        password = it
                        if (uiState is ProfileUiState.Error) viewModel.clearError()
                    },
                    label = { Text(stringResource(Res.string.label_password)) },
                    visualTransformation = PasswordVisualTransformation(),
                    modifier = Modifier.fillMaxWidth(),
                )
                Spacer(modifier = Modifier.height(16.dp))

                Button(
                    onClick = {
                        if (isRegistering) {
                            viewModel.signUpWithEmail(email, password)
                        } else {
                            viewModel.signInWithEmail(email, password)
                        }
                    },
                    modifier = Modifier.fillMaxWidth(),
                    enabled = email.isNotBlank() && password.isNotBlank(),
                ) {
                    Text(if (isRegistering) stringResource(Res.string.btn_signup) else stringResource(Res.string.btn_login))
                }

                Spacer(modifier = Modifier.height(8.dp))

                OutlinedButton(
                    onClick = {
                        isRegistering = !isRegistering
                        viewModel.clearError()
                    },
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    Text(if (isRegistering) stringResource(Res.string.text_have_account_login) else stringResource(Res.string.text_create_account))
                }

                Spacer(modifier = Modifier.height(16.dp))
                Text(stringResource(Res.string.text_or), style = MaterialTheme.typography.bodyMedium)
                Spacer(modifier = Modifier.height(16.dp))

                Button(
                    onClick = { googleSignInLauncher.launch() },
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    Text(stringResource(Res.string.btn_google_signin))
                }
            }
        }
    }
}

@Composable
fun ProfileMenuItem(
    icon: ImageVector,
    text: String,
    onClick: () -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .padding(vertical = 12.dp, horizontal = 8.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Icon(
                imageVector = icon,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            Spacer(modifier = Modifier.width(16.dp))
            Text(
                text = text,
                style = MaterialTheme.typography.bodyLarge,
            )
        }
        Icon(
            imageVector = Icons.AutoMirrored.Filled.KeyboardArrowRight,
            contentDescription = null,
            tint = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
}

